<script setup>
    const route = useRoute();
    const isiFrame = route.path.includes('iframe') ?? false;

    // Editable copy from /admin, stored in D1. `html` is null until someone
    // saves something, in which case the built-in copy below renders instead —
    // an empty table must never blank the top of the homepage. A failed request
    // lands in the same place: `data` stays null and the fallback shows.
    const { data } = await useFetch('/api/page-content/home', {
        key: 'page-content-home',
        default: () => null,
    });
    // The page title is the component's now, so a leading <h1> in the managed
    // copy is a duplicate of it. See app/utils/pageContent.ts.
    const managedHtml = computed(() => {
        const stripped = stripLeadingH1(data.value?.html);
        return stripped.trim() ? stripped : null;
    });
</script>

<template>
    <section>
        <div class="rich-text container hd-container">
            <!-- The site header now carries the OHC wordmark, so repeating it
                 at the top of the homepage is redundant (Asana O2-47). The
                 iframe embed is the exception: its layout is a sub-nav only,
                 with no header and no logo anywhere, so this stays the sole
                 branding there. -->
            <SvgsLogoHorizontal class="logo" v-if="isiFrame" />

            <!-- Hard-coded rather than typed into /admin (Asana O2-47). Editors
                 were reproducing this by hand with inline styles, which could not
                 reach the condensed display face the rest of the page uses. The
                 festival name and dates are also the one thing on this page that
                 must not be accidentally edited away. -->
            <div class="page-title">
                <h1>Open House Chicago</h1>
                <p class="page-date">October 16&ndash;17, 2027</p>
            </div>

            <div v-if="managedHtml" class="content body-book" v-html="managedHtml"></div>
            <div v-else class="content body-book">
                <ul>
                    <li><NuxtLink to="/login" class="body-link login-link">Log in</NuxtLink> <em>before</em> you start saving sites and itineraries, and you’ll be able to access them across devices.</li>
                    <li v-if="!isiFrame">Plus, visit one of our four <NuxtLink to="/about-ohc#headquarters" class="body-link">Neighborhood Headquarters</NuxtLink> across the city to plan your day.</li>
                    <li v-else>Plus, visit one of our four <NuxtLink to="https://www.architecture.org/open-house-chicago/about#headquarters" class="body-link" target="_blank" >Neighborhood Headquarters</NuxtLink> across the city to plan your day.</li>
                </ul>
                <p>
                    <NuxtLink to="/about-ohc" v-if="!isiFrame">Open House Chicago (OHC)</NuxtLink>
                    <NuxtLink to="https://www.architecture.org/open-house-chicago/about" target="_blank" v-else>Open House Chicago (OHC)</NuxtLink>, 
                    one of the largest architecture and urban exploration festivals in the world, returns to celebrate 16
                    years of architecture, culture and community. This free public festival offers behind-the-scenes access to 200+ sites, including many not
                    normally open to the public.</p> 
                <p>Be sure to take photos as you explore for a chance to win the
                    <NuxtLink to="https://www.architecture.org/open-house-chicago/photo-competition-2026">OHC photo competition</NuxtLink>.
                </p>
            </div>
        </div>
    </section>
</template>

<style lang="scss">
    .rich-text {
        // Sized off the mockup on O2-47: deliberately larger than .headline-1
        // (32px), which is what the section headings below it use, so the page
        // title reads as the top of the hierarchy rather than a peer.
        .page-title {
            margin-bottom: 32px;

            h1 {
                font-family: $title;
                font-weight: $bold;
                font-size: 44px;
                line-height: 1em;
                text-transform: uppercase;
                margin-bottom: 8px;
            }

            .page-date {
                font-family: $title;
                font-weight: $bold;
                font-size: 22px;
                line-height: 1em;
                text-transform: uppercase;
                color: $red;
                margin-bottom: 0;
            }
        }

        // The logo used to supply this via its own top margin. With it hidden
        // outside the iframe the h1 sat flush against the nav, so the spacing
        // lives on the block now and holds in both cases.
        //
        // Qualified with .hd-container deliberately: that class sets the
        // `padding: 0 16px` SHORTHAND, which resets padding-top to 0 and wins
        // on cascade order at equal specificity. A bare `.rich-text` rule here
        // computes to 0px.
        &.hd-container {
            padding-top: 32px;
        }

        &:not(:first-child) {
            margin-top: 60px;
        }

        h1 {
            font-family: $avenir-next, sans-serif;
            text-transform: uppercase;
            margin-bottom: 16px;
            font-size: 1.5em;
        }

        h2 {
            font-family: $avenir-next, sans-serif;
            text-transform: uppercase;
            margin-bottom: 8px;
        }        

        ul {
            margin-bottom: 16px;
            list-style: disc;
            padding-left: 20px;

            li {
                margin-bottom: 4px;

                &::marker {
                    color: $turquoise;
                }

                em {
                    font-weight: 500;
                }
            }
        }

        .logo {
            width: 80%;
            max-width: 600px;
            height: auto;
            // Top spacing comes from .rich-text's padding-top now.
            margin: 0 auto 32px auto;
        }

        p {
            margin-bottom: 1rem;
            line-height: 1.561;

            strong {
                font-weight: 800;
            }

            em {
                font-style: normal;
                font-weight: 800;
            }

        }

        // ONE treatment for every link in editor copy (Asana O2-67).
        //
        // Paragraph links were underlined and list links were red, unbolded and
        // undecorated — so the same link read two different ways in one block of
        // copy, a bullet directly beneath the paragraph that styled it the other
        // way. Editors have no way to reconcile that from /admin; it was purely
        // a function of which element the link happened to sit in.
        p a,
        li a {
            text-decoration: underline;

            &:hover {
                color: $red;
                text-decoration: none;
            }

            // The one deliberate exception, and only in the built-in fallback
            // copy below: the login prompt is a call to action rather than a
            // reference, and carries the class to say so.
            &.login-link {
                color: $red;
                font-weight: 600;
                text-decoration: none;

                &:hover {
                    color: $turquoise;
                }
            }
        }
    }
</style>
